import type { CompatibilityInfo, CrossplayStatus, Game } from "../types/game";

const RAWG_KEY = import.meta.env.VITE_RAWG_API_KEY as string | undefined;

// --- RAWG: catalog, search, filters -----------------------------------------

interface RawgGame {
  id: number;
  name: string;
  released: string | null;
  background_image: string | null;
  rating: number;
  added?: number;
  metacritic?: number | null;
  parent_platforms?: Array<{ platform: { id: number; name: string } }>;
  platforms?: Array<{ platform: { name: string } }>;
  genres?: Array<{ id: number; name: string }>;
  tags?: Array<{ name: string; slug: string }>;
  stores?: Array<{ store: { name: string } }>;
  short_screenshots?: Array<{ image: string }>;
}

interface RawgListResponse {
  results: RawgGame[];
}

function normalizeRawg(g: RawgGame): Game {
  return {
    id: `rawg:${g.id}`,
    source: "rawg",
    sourceId: String(g.id),
    title: g.name,
    platforms: g.platforms?.map((p) => p.platform.name) ?? [],
    genres: g.genres?.map((x) => x.name) ?? [],
    tags: g.tags?.map((t) => t.name),
    releaseYear: g.released ? Number(g.released.slice(0, 4)) : undefined,
    imageUrl: g.background_image ?? undefined,
    rating: g.rating,
    stores: g.stores?.map((s) => s.store.name),
    screenshots: g.short_screenshots?.map((s) => s.image),
  };
}

export interface SearchOptions {
  query?: string;
  genreId?: string;
  /** Specific RAWG platform id (e.g. PS5 = 187, Switch = 7). */
  platformId?: string;
  tags?: string[];
  pageSize?: number;
}

// DLC, soundtracks, etc. clutter the catalog. RAWG tags them, and we also
// catch obvious DLC titles by name as a fallback.
const DLC_TAG_SLUGS = new Set([
  "dlc",
  "expansion-pack",
  "expansion",
  "soundtrack",
  "ost",
]);
const DLC_TITLE_RE =
  /\b(dlc|expansion(?: pack)?|map pack|season pass|soundtrack|ost|bonus content|character pack)\b/i;

function isLikelyDlc(g: RawgGame): boolean {
  if (g.tags?.some((t) => DLC_TAG_SLUGS.has(t.slug))) return true;
  if (DLC_TITLE_RE.test(g.name)) return true;
  return false;
}

// RAWG store ids for "real" storefronts: Steam(1), Xbox(2), PlayStation(3),
// GOG(5), Nintendo(6), Xbox 360(7), Epic(11). Deliberately excludes
// itch.io(9), App Store(4) and Google Play(8) so the catalog stays focused
// on PC (Steam/Epic/GOG) and the major consoles.
const MAJOR_STORE_IDS = "1,2,3,5,6,7,11";

// Drop long-tail junk almost nobody has in a collection (e.g. obscure
// itch.io fan entries that text-match a famous title). Kept low so legit
// smaller games survive; there's a fallback below if it's too aggressive.
const POPULARITY_FLOOR = 30;

export async function searchGames(opts: SearchOptions): Promise<Game[]> {
  if (!RAWG_KEY) {
    throw new Error(
      "Missing VITE_RAWG_API_KEY. Set it in Cloudflare (and redeploy) or in your local .env.",
    );
  }
  const url = new URL("https://api.rawg.io/api/games");
  url.searchParams.set("key", RAWG_KEY);
  // Over-fetch a bit so the filters still leave a full grid.
  url.searchParams.set("page_size", String(opts.pageSize ?? 40));
  if (opts.query?.trim()) url.searchParams.set("search", opts.query.trim());
  if (opts.genreId) url.searchParams.set("genres", opts.genreId);
  if (opts.platformId) url.searchParams.set("platforms", opts.platformId);
  if (opts.tags?.length) url.searchParams.set("tags", opts.tags.join(","));
  // Restrict to real storefronts (no itch.io / mobile-only).
  url.searchParams.set("stores", MAJOR_STORE_IDS);
  // Server-side hint to skip DLC where supported.
  url.searchParams.set("exclude_additions", "true");
  // For browsing (no text query) rank by user-collection count = real
  // popularity. `-rating` over-favors niche games with a few perfect votes.
  if (!opts.query?.trim()) url.searchParams.set("ordering", "-added");

  const res = await fetch(url.toString());
  if (!res.ok) {
    throw new Error(`RAWG request failed: ${res.status} ${res.statusText}`);
  }
  const data = (await res.json()) as RawgListResponse;
  const noDlc = data.results.filter((g) => !isLikelyDlc(g));
  // Apply the popularity floor, but fall back to the un-floored list if it
  // would leave too few results (legit niche searches shouldn't come back empty).
  const popular = noDlc.filter((g) => (g.added ?? 0) >= POPULARITY_FLOOR);
  const final = popular.length >= 3 ? popular : noDlc;
  return final.slice(0, 24).map(normalizeRawg);
}

export interface GenreOption {
  id: string;
  name: string;
}

export async function getGenres(): Promise<GenreOption[]> {
  if (!RAWG_KEY) return [];
  const url = new URL("https://api.rawg.io/api/genres");
  url.searchParams.set("key", RAWG_KEY);
  const res = await fetch(url.toString());
  if (!res.ok) return [];
  const data = (await res.json()) as {
    results: Array<{ id: number; name: string }>;
  };
  return data.results.map((g) => ({ id: String(g.id), name: g.name }));
}

// RAWG specific-platform ids (stable). Limited to current and last-gen
// hardware where a typical player is actually shopping for new games.
export const PLATFORM_OPTIONS: GenreOption[] = [
  { id: "4", name: "PC" },
  { id: "187", name: "PlayStation 5" },
  { id: "18", name: "PlayStation 4" },
  { id: "186", name: "Xbox Series X/S" },
  { id: "1", name: "Xbox One" },
  { id: "7", name: "Nintendo Switch" },
];

// --- Enrichment: IGDB (co-op / players) + Steam (crossplay / trailer) --------

interface IgdbGame {
  name: string;
  summary?: string;
  multiplayer_modes?: Array<{
    onlinecoop?: boolean;
    offlinecoop?: boolean;
    splitscreen?: boolean;
    onlinemax?: number;
    offlinemax?: number;
  }>;
}

interface SteamHit {
  details?: {
    short_description?: string;
    categories?: Array<{ description: string }>;
    movies?: Array<{ webm?: { max: string }; mp4?: { max: string } }>;
  };
}

const compatCache = new Map<string, CompatibilityInfo>();

async function fetchIgdb(title: string): Promise<IgdbGame | null> {
  try {
    const res = await fetch("/api/igdb", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ query: title }),
    });
    if (!res.ok) return null;
    const arr = (await res.json()) as IgdbGame[];
    return arr[0] ?? null;
  } catch {
    return null;
  }
}

async function fetchSteam(title: string): Promise<SteamHit | null> {
  try {
    const res = await fetch(`/api/steam?q=${encodeURIComponent(title)}`);
    if (!res.ok) return null;
    const arr = (await res.json()) as SteamHit[];
    return arr[0] ?? null;
  } catch {
    return null;
  }
}

/**
 * Best-effort multiplayer/crossplay enrichment for a game title.
 * Degrades gracefully: if a provider is unconfigured or fails, its fields
 * are simply omitted rather than throwing.
 */
export async function getCompatibility(title: string): Promise<CompatibilityInfo> {
  const cached = compatCache.get(title);
  if (cached) return cached;

  const [igdb, steam] = await Promise.all([
    fetchIgdb(title),
    fetchSteam(title),
  ]);

  const sources: string[] = [];
  const info: CompatibilityInfo = { crossplayStatus: "unknown", sources };

  // IGDB multiplayer modes (most reliable structured co-op data)
  const mm = igdb?.multiplayer_modes?.[0];
  if (igdb) {
    sources.push("igdb");
    if (igdb.summary) info.description = igdb.summary;
  }
  if (mm) {
    if (mm.onlinecoop !== undefined) info.onlineCoop = mm.onlinecoop;
    if (mm.offlinecoop !== undefined) info.localCoop = mm.offlinecoop;
    if (mm.splitscreen !== undefined) info.splitScreen = mm.splitscreen;
    if (mm.onlinemax) info.maxOnlinePlayers = mm.onlinemax;
    if (mm.offlinemax) info.maxLocalPlayers = mm.offlinemax;
  }

  // Steam categories: crossplay signal + co-op fallback + trailer
  const cats = steam?.details?.categories?.map((c) => c.description) ?? [];
  if (steam?.details) {
    sources.push("steam");
    if (!info.description && steam.details.short_description) {
      info.description = steam.details.short_description;
    }
    if (info.onlineCoop === undefined)
      info.onlineCoop = cats.some((c) => /online co-?op/i.test(c));
    if (info.localCoop === undefined)
      info.localCoop = cats.some((c) =>
        /(local|lan|shared|split.?screen) co-?op/i.test(c),
      );
    if (info.splitScreen === undefined)
      info.splitScreen = cats.some((c) => /split.?screen/i.test(c));

    const movie = steam.details.movies?.[0];
    info.trailerUrl = movie?.mp4?.max ?? movie?.webm?.max;
  }

  // Crossplay: the only automated signal we have today is Steam's tag.
  const steamCrossplay = cats.some((c) =>
    /cross-?platform multiplayer/i.test(c),
  );
  info.crossplayStatus = deriveCrossplay(steamCrossplay);

  compatCache.set(title, info);
  return info;
}

function deriveCrossplay(steamTagPresent: boolean): CrossplayStatus {
  // Absence of the Steam tag does NOT prove "no" (the tag is incomplete and
  // PC-only), so we fall back to "unknown" rather than claiming "no".
  return steamTagPresent ? "yes-unknown" : "unknown";
}

export function crossplayLabel(status: CrossplayStatus): string {
  switch (status) {
    case "yes-confirmed":
      return "Yes — crossplay supported";
    case "yes-unknown":
      return "Yes — crossplay console unknown";
    case "no":
      return "No, not available";
    case "unknown":
      return "N/A — no crossplay info";
  }
}
